import {type ReactNode} from 'react';
import {User, Lock, BadgeCheck} from 'lucide-react';
import {Badge, Card, CardContent, CardFooter} from '@kroll-resolver-crisp/ui-components-react';
import {CopyableUrl} from '@/components/shared/CopyableUrl';
import {type PinterestProfileStatus} from '@/api/pinterest';
import {
  DetailField,
  MetaGrid,
  PlatformStatus,
  PriorSubmissions
} from '@/components/channels/StatusCardParts';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {formatCompactNumber} from '@/lib/itemMetadata';
import {cn} from '@/lib/utils';

function formatDate(iso: string | null): string {
  if (!iso) {
    return '—';
  }

  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}

// Stats/bio/created_at/website come from Pinterest's own page-embedded state and are best-effort -
// see PinterestProfilePageParser. Any of them can be null even for a real profile.
export function PinterestProfileCard({
  profile,
  borderClassName,
  priorSubmissionCount,
  footer
}: {
  readonly profile: PinterestProfileStatus;
  // The "already submitted" emphasis the page computes from the profile's prior submissions.
  readonly borderClassName?: string;
  readonly priorSubmissionCount?: number;
  // The profile's own actions (flag, classify, or the submitted state), rendered in the card footer.
  readonly footer?: ReactNode;
}) {
  const followerCount = formatCompactNumber(profile.follower_count);
  const followingCount = formatCompactNumber(profile.following_count);
  const pinCount = formatCompactNumber(profile.pin_count);
  const boardCount = formatCompactNumber(profile.board_count);
  const metaLine = [
    `@${profile.username}`,
    profile.created_at ? `Joined ${formatDate(profile.created_at)}` : null,
    followerCount ? `${followerCount} followers` : null,
    followingCount ? `${followingCount} following` : null,
    pinCount ? `${pinCount} pins` : null,
    boardCount ? `${boardCount} boards` : null
  ].filter(Boolean);

  return (
    <Card className={cn('border', borderClassName ?? 'border-border')}>
      <CardContent className="flex flex-col gap-4">
        <div className="flex gap-4">
          <div className="group bg-card relative aspect-square w-50 shrink-0 overflow-hidden">
            {profile.avatar_url ? (
              <SensitiveMedia
                src={profile.avatar_url}
                alt={profile.display_name ?? profile.username}
                className="size-full object-cover"
              />
            ) : (
              <span className="text-muted-foreground flex size-full items-center justify-center">
                <User className="size-8" />
              </span>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-foreground truncate text-sm font-medium">
                  {profile.display_name ?? profile.username}
                </p>
                <p className="text-muted-foreground truncate text-xs">{metaLine.join(' · ')}</p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                <Badge variant="outline">
                  <User />
                  Actor
                </Badge>
                {profile.is_verified_merchant ? (
                  <Badge variant="outline">
                    <BadgeCheck />
                    Verified merchant
                  </Badge>
                ) : null}
                {profile.is_private_profile ? (
                  <Badge variant="outline">
                    <Lock />
                    Private
                  </Badge>
                ) : null}
              </div>
            </div>
            {profile.bio ? (
              <p className="text-muted-foreground line-clamp-2 text-xs">{profile.bio}</p>
            ) : null}
            {profile.profile_url ? (
              <CopyableUrl url={profile.profile_url} label="Copy profile URL" className="text-xs" />
            ) : null}
          </div>
        </div>

        <MetaGrid className="border-border border-t pt-4">
          <DetailField label="External ID">
            <span className="font-mono">{profile.user_id ?? profile.username}</span>
          </DetailField>
          <DetailField label="Platform status">
            <PlatformStatus
              status={
                profile.is_private_profile === null
                  ? null
                  : profile.is_private_profile
                    ? 'private'
                    : 'public'
              }
            />
          </DetailField>
          <DetailField label="Username">
            <span className="font-mono">@{profile.username}</span>
          </DetailField>
          <DetailField label="Joined">{formatDate(profile.created_at)}</DetailField>
          <DetailField label="Website" className="col-span-2">
            {profile.website_url ? (
              <CopyableUrl url={profile.website_url} label="Copy website URL" />
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </DetailField>
          {priorSubmissionCount === undefined ? null : (
            <DetailField label="Prior submissions">
              <PriorSubmissions count={priorSubmissionCount} />
            </DetailField>
          )}
        </MetaGrid>
      </CardContent>
      {footer ? <CardFooter className="justify-end gap-2">{footer}</CardFooter> : null}
    </Card>
  );
}
